// apps/academy/src/lib/api-academy.ts
import { api, unwrapResponse as unwrap } from '@neuroforge/shared/client'

// ─── Types réponse API ───

export interface CourseListItem {
  id: string
  title: string
  slug: string
  description: string
  thumbnailUrl?: string | null
  difficulty: number
  tags: string[]
  totalModules: number
  totalLessons: number
  estimatedMinutes: number
  status: string
  isPublished: boolean
  order: number
  publishedAt: string | null
  createdAt: string
}

export interface CourseDetail extends CourseListItem {
  modules: CourseModuleDetail[]
}

export interface CourseModuleDetail {
  id: string
  title: string
  slug: string
  description: string | null
  order: number
  status: string
  totalLessons: number
  lessons: LessonListItem[]
}

export interface LessonListItem {
  id: string
  title: string
  slug: string
  type: string // 'ARTICLE' | 'VIDEO' | 'EXERCISE' | 'QUIZ'
  status: string // 'DRAFT' | 'PUBLISHED' | 'ARCHIVED'
  content: string | null
  estimatedMinutes: number
  difficulty?: number
  order: number
  isFree: boolean
  isPremium?: boolean
  isExpertOnly: boolean
}

export interface LessonDetail {
  id: string
  title: string
  slug: string
  type: string
  content: string | null
  videoUrl: string | null
  estimatedMinutes: number
  difficulty: number
  isFree: boolean
  isExpertOnly: boolean
  /** API lessons/by-slug gate; never infer grants from this UI field. */
  accessLevel?: 'requires_auth' | 'requires_premium' | 'full'
  module: { id: string; title: string; slug: string }
  course: { id: string; title: string; slug: string }
  nextLesson: { slug: string; title: string } | null
  prevLesson: { slug: string; title: string } | null
}

export interface ProgressItem {
  lessonId: string
  courseId: string
  status: string // 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED'
  completedAt: string | null
  timeSpentMinutes: number
}

export interface CourseProgressSummary {
  courseId: string
  courseTitle: string
  courseSlug: string
  completedLessons: number
  totalLessons: number
  percentage: number
  lastActivityAt: string
}

export interface CourseListResponse {
  data: CourseListItem[]
  meta: { total: number; page: number; limit: number; totalPages: number }
}

// ─── API Calls ───

export async function fetchCourses(
  params: {
    difficulty?: number
    tag?: string
    page?: number
    limit?: number
    sort?: 'order' | 'recent' | 'difficulty'
  } = {},
  signal?: AbortSignal,
): Promise<CourseListResponse> {
  const res = await api.get('/courses', { params, signal })
  // L'API retourne un tableau brut (pas paginé) — on wrap dans CourseListResponse
  const items = unwrap<CourseListItem[]>(res)
  return {
    data: items,
    meta: { total: items.length, page: 1, limit: items.length, totalPages: 1 },
  }
}

export async function fetchCourseBySlug(slug: string, signal?: AbortSignal): Promise<CourseDetail> {
  const res = await api.get(`/courses/${slug}`, { signal })
  return unwrap(res)
}

export async function fetchLessonBySlug(
  lessonSlug: string,
  courseSlug: string,
  signal?: AbortSignal,
): Promise<LessonDetail> {
  const res = await api.get(`/lessons/by-slug/${lessonSlug}`, {
    params: { course: courseSlug },
    signal,
  })
  return unwrap(res)
}

export async function fetchProgress(signal?: AbortSignal): Promise<ProgressItem[]> {
  // Progression par leçon (items bruts) — distinct de l'overview par cours.
  const res = await api.get('/progress/lessons', { signal })
  return unwrap(res)
}

export async function fetchProgressOverview(
  signal?: AbortSignal,
): Promise<CourseProgressSummary[]> {
  const res = await api.get('/progress', { signal })
  // L'API overview retourne un tableau de CourseProgressSummary
  return unwrap<CourseProgressSummary[]>(res)
}

export async function markLessonComplete(lessonId: string): Promise<{ status: string }> {
  const res = await api.post(`/progress/lesson/${lessonId}`)
  return unwrap(res)
}

// ─── Admin API Calls ───

export async function fetchCourseBySlugAdmin(
  slug: string,
  signal?: AbortSignal,
): Promise<CourseDetail> {
  const res = await api.get(`/courses/admin/${slug}`, { signal })
  return unwrap<CourseDetail>(res)
}

export async function fetchAllCoursesAdmin(signal?: AbortSignal): Promise<CourseListItem[]> {
  const res = await api.get('/courses/all', { signal })
  return unwrap<CourseListItem[]>(res)
}

export async function adminCreateCourse(data: {
  title: string
  description: string
  difficulty?: number
  tags?: string[]
  order?: number
}): Promise<CourseDetail> {
  const res = await api.post('/courses', data)
  return unwrap<CourseDetail>(res)
}

export async function adminUpdateCourse(
  id: string,
  data: Record<string, unknown>,
): Promise<CourseDetail> {
  const res = await api.patch(`/courses/${id}`, data)
  return unwrap<CourseDetail>(res)
}

export async function adminDeleteCourse(id: string): Promise<void> {
  await api.delete(`/courses/${id}`)
}

export async function adminCreateModule(data: {
  courseId: string
  title: string
  slug: string
  description?: string
  order?: number
}): Promise<CourseModuleDetail> {
  const res = await api.post('/modules', data)
  return unwrap<CourseModuleDetail>(res)
}

export async function adminUpdateModule(
  id: string,
  data: Record<string, unknown>,
): Promise<CourseModuleDetail> {
  const res = await api.patch(`/modules/${id}`, data)
  return unwrap<CourseModuleDetail>(res)
}

export async function adminDeleteModule(id: string): Promise<void> {
  await api.delete(`/modules/${id}`)
}

export async function adminCreateLesson(data: {
  moduleId: string
  title: string
  slug: string
  type?: string
  content?: string
  estimatedMinutes?: number
  difficulty?: number
  order?: number
  isFree?: boolean
}): Promise<LessonDetail> {
  const res = await api.post('/lessons', data)
  return unwrap<LessonDetail>(res)
}

export async function adminUpdateLesson(
  id: string,
  data: Record<string, unknown>,
): Promise<LessonDetail> {
  const res = await api.patch(`/lessons/${id}`, data)
  return unwrap<LessonDetail>(res)
}

export async function adminDeleteLesson(id: string): Promise<void> {
  await api.delete(`/lessons/${id}`)
}

// ─── Academy K: Catalogue ───

export interface CatalogCourseItem {
  id: string
  slug: string
  title: string
  description: string
  difficulty: number
  tags: string[]
  totalModules: number
  totalLessons: number
  estimatedMinutes: number
  isDefault: boolean
  isEnrolled: boolean
  canUnenroll: boolean
}

export async function fetchCatalog(signal?: AbortSignal): Promise<CatalogCourseItem[]> {
  const res = await api.get('/courses/catalog', { signal })
  return unwrap(res)
}

// ─── Academy K: Enrollment ───

export async function enrollCourse(courseId: string): Promise<{ enrolled: boolean }> {
  const res = await api.post(`/courses/${courseId}/enroll`)
  return unwrap(res)
}

export async function unenrollCourse(courseId: string): Promise<void> {
  await api.delete(`/courses/${courseId}/enroll`)
}

// ─── Sidebar navigation data (cours + modules + leçons + progression) ───

export interface CourseNavItem {
  id: string
  slug: string
  title: string
  difficulty: number
  isDefault: boolean // Academy K: cours par défaut (toujours affiché)
  totalLessons: number
  completedLessons: number
  modules: Array<{
    id: string
    title: string
    order: number
    lessons: Array<{
      id: string
      slug: string
      title: string
      isFree: boolean // Academy K: leçon gratuite (🟢)
      isPremium: boolean // Academy K: leçon premium (💎)
      isCompleted: boolean
    }>
  }>
}

export async function fetchCourseNavData(signal?: AbortSignal): Promise<{
  courses: CourseNavItem[]
  globalProgress: number
  totalLessons: number
  completedLessons: number
}> {
  // 1 seul appel API — le backend retourne tout (cours + modules + leçons + progression)
  // Endpoint : GET /courses/nav (Phase F — élimine le N+1)
  const res = await api.get('/courses/nav', { signal })
  return unwrap(res)
}
